import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from "@theme/dialog";
import { ThemeEvents } from "@theme/events";
import { isMobileBreakpoint } from "@theme/utilities";

/** Query param the retired `/cart` page redirects with (see `sections/main-cart.liquid`). */
const OPEN_CART_PARAM = "open_cart";

/** Upper bound on how long to wait for the quick-add or quick shop dialog to finish closing. */
const DIALOG_CLOSE_TIMEOUT = 1000;

/** How long the drawer stays open after an add to cart before closing on its own. */
const AUTO_CLOSE_DELAY = 2000;
const AUTO_CLOSE_CANCEL_EVENTS = ["pointermove", "pointerdown", "focusin", "scroll"];

/**
 * The cart drawer.
 *
 * Opens from the header icon (`on:click="#cart-drawer/open"`), on every successful add to
 * cart, and when the page loads with `?open_cart=true`. Quantity changes and removals inside
 * the drawer also fire `cart:update`, so the handler only reacts to product-form sources.
 *
 * An add-to-cart open is transient: it closes itself after `AUTO_CLOSE_DELAY` unless the user
 * interacts with the drawer first. The icon and query-param opens stay until dismissed.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoCloseTimer = null;

  /** @type {AbortController | null} */
  #autoCloseAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }

    this.#openFromQueryParam();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.#historyAbortController?.abort();
    this.#cancelAutoClose();
  }

  /**
   * Opens the drawer when the URL carries `?open_cart=true`, then strips the param so a reload
   * or the mobile `pushState` below doesn't carry it forward.
   */
  #openFromQueryParam() {
    const url = new URL(window.location.href);
    if (url.searchParams.get(OPEN_CART_PARAM) !== "true") return;

    url.searchParams.delete(OPEN_CART_PARAM);
    history.replaceState(history.state, "", url);
    this.open();
  }

  /**
   * Opens the drawer after a successful add to cart and announces the new count.
   * @param {Event} event - A `cart:update` event (`CartAddEvent` or `CartUpdateEvent`).
   */
  #handleCartUpdate = async (event) => {
    const { data, resource } =
      /** @type {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, source?: string } }>} */ (
        event
      ).detail ?? {};

    this.#announceCartCount(resource?.item_count);

    if (data?.didError) return;
    // `cart-items-component` (quantity change / remove) also dispatches `cart:update`.
    if (data?.source !== "product-form-component") return;

    await this.#waitForQuickAddToClose();
    await this.#waitForQuickShopToClose();
    this.#openTransient();
  };

  /**
   * Opens the drawer and arms the auto-close. The timer starts once the dialog is actually
   * shown (`showDialog` defers to the next frame), and after `showModal` has moved focus inside,
   * so that initial focus doesn't count as user interaction.
   */
  #openTransient() {
    if (this.refs.dialog?.open) {
      this.#armAutoClose();
      return;
    }

    this.addEventListener(DialogOpenEvent.eventName, () => this.#armAutoClose(), { once: true });
    this.open();
  }

  #armAutoClose() {
    this.#cancelAutoClose();

    this.#autoCloseTimer = setTimeout(() => {
      this.#autoCloseTimer = null;
      this.closeDialog();
    }, AUTO_CLOSE_DELAY);

    // `scroll` doesn't bubble, so listen in the capture phase to catch the scroll body.
    this.#autoCloseAbortController = new AbortController();
    const { signal } = this.#autoCloseAbortController;
    for (const eventName of AUTO_CLOSE_CANCEL_EVENTS) {
      this.refs.dialog.addEventListener(eventName, this.#handleAutoCloseInteraction, { capture: true, signal });
    }
  }

  /**
   * Pointer events over the `::backdrop` also target the dialog, so a pointer anywhere on the
   * page would cancel the timer the moment the drawer opens. Only count pointers inside its box.
   * @param {Event} event
   */
  #handleAutoCloseInteraction = (event) => {
    if (event instanceof PointerEvent && !this.#isPointerInsideDialog(event)) return;
    this.#cancelAutoClose();
  };

  /** @param {PointerEvent} event */
  #isPointerInsideDialog(event) {
    const { left, right, top, bottom } = this.refs.dialog.getBoundingClientRect();
    return event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;
  }

  #cancelAutoClose() {
    if (this.#autoCloseTimer) clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = null;
    this.#autoCloseAbortController?.abort();
    this.#autoCloseAbortController = null;
  }

  /**
   * The quick-add modal closes itself on the same `cart:update`. Wait for it so the two dialogs
   * don't stack in the top layer and so its scroll-lock teardown finishes before ours starts.
   */
  async #waitForQuickAddToClose() {
    const quickAdd = /** @type {(HTMLElement & { refs?: { dialog?: HTMLDialogElement } }) | null} */ (
      document.getElementById("quick-add-dialog")
    );
    await this.#waitForDialogToClose(quickAdd);
  }

  /**
   * The quick shop modal (`quickshop-dialog`, one per product card) closes itself on the same
   * `cart:update`. Wait for whichever instance is open, same reasoning as quick-add above.
   */
  async #waitForQuickShopToClose() {
    const quickShopDialogs = /** @type {NodeListOf<HTMLElement & { refs?: { dialog?: HTMLDialogElement } }>} */ (
      document.querySelectorAll("quickshop-dialog")
    );
    const openQuickShop = Array.from(quickShopDialogs).find((dialog) => dialog.refs?.dialog?.open);
    await this.#waitForDialogToClose(openQuickShop);
  }

  /**
   * @param {(HTMLElement & { refs?: { dialog?: HTMLDialogElement } }) | undefined | null} dialogComponent
   */
  async #waitForDialogToClose(dialogComponent) {
    if (!dialogComponent?.refs?.dialog?.open) return;

    await new Promise((resolve) => {
      const timeout = setTimeout(resolve, DIALOG_CLOSE_TIMEOUT);
      dialogComponent.addEventListener(
        DialogCloseEvent.eventName,
        () => {
          clearTimeout(timeout);
          resolve(undefined);
        },
        { once: true }
      );
    });
  }

  #handleOpen = () => {
    // `theme-editor.js` reads this to reopen the drawer after a section re-render.
    this.setAttribute("open", "");

    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, "");
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener("popstate", this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleClose = () => {
    this.removeAttribute("open");
    this.#historyAbortController?.abort();
    this.#cancelAutoClose();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      await this.closeDialog();
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
